/**
 * AgendaBlockDialog — Cria bloqueios de agenda (pontuais ou recorrentes).
 *
 * Escopo:
 *  - "tenant" = toda a empresa (feriado, reunião, sistema fechado)
 *  - "professional" = um profissional específico
 *
 * Tipos:
 *  - "one_off" = bloqueio pontual (data + intervalo de horas)
 *  - "recurring" = regra recorrente (dias da semana + intervalo de horas)
 *
 * Bloqueios criados são respeitados pelo backend nos endpoints:
 *  - POST /tenant/appointments (rejeita com 409)
 *  - GET /public/booking/{slug}/slots (esconde horários bloqueados)
 */
import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Ban, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const WEEKDAYS = [
  { idx: 0, short: "Seg" },
  { idx: 1, short: "Ter" },
  { idx: 2, short: "Qua" },
  { idx: 3, short: "Qui" },
  { idx: 4, short: "Sex" },
  { idx: 5, short: "Sáb" },
  { idx: 6, short: "Dom" },
];

const emptyForm = {
  scope: "tenant",
  professional_id: "",
  type: "one_off",
  reason: "",
  date: "",
  start_time: "09:00",
  end_time: "18:00",
  weekdays: [],
  starts_on: "",
  ends_on: "",
};

export default function AgendaBlockDialog({ open, onOpenChange, resources, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(emptyForm);
  }, [open]);

  const toggleWeekday = (idx) => {
    setForm((f) => ({
      ...f,
      weekdays: f.weekdays.includes(idx) ? f.weekdays.filter((w) => w !== idx) : [...f.weekdays, idx].sort(),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        scope: form.scope,
        type: form.type,
        reason: form.reason || null,
      };
      if (form.scope === "professional") {
        if (!form.professional_id) {
          toast.error("Selecione o profissional.");
          setBusy(false); return;
        }
        payload.professional_id = form.professional_id;
      }
      if (form.type === "one_off") {
        if (!form.date || !form.start_time || !form.end_time) {
          toast.error("Preencha data e horários.");
          setBusy(false); return;
        }
        payload.start_at = `${form.date}T${form.start_time}:00`;
        payload.end_at = `${form.date}T${form.end_time}:00`;
      } else {
        if (form.weekdays.length === 0) {
          toast.error("Selecione ao menos um dia da semana.");
          setBusy(false); return;
        }
        payload.weekdays = form.weekdays;
        payload.start_time = form.start_time;
        payload.end_time = form.end_time;
        if (form.starts_on) payload.starts_on = form.starts_on;
        if (form.ends_on) payload.ends_on = form.ends_on;
      }
      await api.post("/tenant/agenda/blocks", payload);
      toast.success("Bloqueio criado");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="block-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Ban className="w-4 h-4 text-red-600" /> Bloquear horário
          </DialogTitle>
          <DialogDescription>
            Impede novos agendamentos no intervalo — para reuniões, folgas, feriados etc.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {/* Escopo */}
          <div className="space-y-1.5">
            <Label>Escopo</Label>
            <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v })}>
              <SelectTrigger data-testid="block-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tenant" data-testid="block-scope-tenant">Toda a empresa</SelectItem>
                <SelectItem value="professional" data-testid="block-scope-professional">Um profissional específico</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.scope === "professional" && (
            <div className="space-y-1.5">
              <Label>Profissional</Label>
              <Select value={form.professional_id} onValueChange={(v) => setForm({ ...form, professional_id: v })}>
                <SelectTrigger data-testid="block-professional"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {resources.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Tipo */}
          <div className="space-y-1.5">
            <Label>Frequência</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="block-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="one_off" data-testid="block-type-one-off">Pontual (data única)</SelectItem>
                <SelectItem value="recurring" data-testid="block-type-recurring">Recorrente (dias da semana)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.type === "one_off" ? (
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Data</Label>
                <Input type="date" data-testid="block-date"
                  value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Início</Label>
                <Input type="time" data-testid="block-start-time"
                  value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Fim</Label>
                <Input type="time" data-testid="block-end-time"
                  value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} required />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Dias da semana</Label>
                <div className="flex flex-wrap gap-1.5" data-testid="block-weekdays">
                  {WEEKDAYS.map((w) => {
                    const active = form.weekdays.includes(w.idx);
                    return (
                      <button
                        key={w.idx}
                        type="button"
                        data-testid={`block-weekday-${w.idx}`}
                        onClick={() => toggleWeekday(w.idx)}
                        className={`px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${
                          active
                            ? "bg-red-500/10 text-red-700 border-red-500/40"
                            : "border-border text-muted-foreground hover:bg-muted"
                        }`}
                      >
                        {w.short}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Início do intervalo</Label>
                  <Input type="time" data-testid="block-start-time"
                    value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} required />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Fim do intervalo</Label>
                  <Input type="time" data-testid="block-end-time"
                    value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Começa em (opcional)</Label>
                  <Input type="date" data-testid="block-starts-on"
                    value={form.starts_on} onChange={(e) => setForm({ ...form, starts_on: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Termina em (opcional)</Label>
                  <Input type="date" data-testid="block-ends-on"
                    value={form.ends_on} onChange={(e) => setForm({ ...form, ends_on: e.target.value })} />
                </div>
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs">Motivo (opcional)</Label>
            <Input placeholder="Ex: Reunião, folga, feriado…" data-testid="block-reason"
              value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} maxLength={200} />
          </div>

          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="block-submit" className="gap-2 bg-red-600 hover:bg-red-700 text-white">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Bloquear
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
